import React, { useEffect, useMemo, useState } from "react"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  Card, CardBody, Col, Row, Input, Table, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Button,
} from "reactstrap"
import { fetchChequeTracking, clearCheque } from "../../../services/finance/finance"
import { toastError, toastSuccess } from "../../../helpers/msg_helper"

const money = v =>
  v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = s => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "—")
const d10 = s => (s ? String(s).slice(0, 10) : "")
const today = () => new Date().toISOString().slice(0, 10)

/**
 * Finance → Cheque Tracking (Cheques Issued register). A payee-oriented log of every issued
 * cheque with its cleared/pending status. Distinct from Bank Reconciliation. Scoped by the active
 * company/group selection (X-Scope headers). Filter by status + free-text search.
 */
const ChequeTracking = () => {
  document.title = "Cheque Reconciliation | Calyvern"
  const thisYear = new Date().getFullYear()
  const [status, setStatus] = useState("all")
  const [year, setYear] = useState("")
  const [search, setSearch] = useState("")
  const [data, setData] = useState({ rows: [], total: 0, count: 0 })
  const [loading, setLoading] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      const p = new URLSearchParams()
      if (status && status !== "all") p.set("status", status)
      if (year) p.set("year", year)
      if (search.trim()) p.set("search", search.trim())
      const r = await fetchChequeTracking(p.toString())
      setData(r?.data || { rows: [], total: 0, count: 0 })
    } catch (e) {
      toastError(e?.message || "Failed to load cheque tracking")
    } finally {
      setLoading(false)
    }
  }

  // reload on status/year change immediately; debounce free-text search
  useEffect(() => { load() /* eslint-disable-next-line */ }, [status, year])
  useEffect(() => {
    const t = setTimeout(load, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line
  }, [search])

  const statusColor = s => (s === "Cleared" ? "#16a34a" : s === "PDC" ? "#7c3aed" : "#dc2626")

  // Record clearance through a small form: cleared date + remarks. Saving stamps the cheque's
  // bank date, which is what flips its status to Cleared. The same form re-opens on a cleared
  // cheque to correct the date/remarks.
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ row: null, cleared_date: "", remarks: "" })
  const [saving, setSaving] = useState(false)

  const openClear = row => {
    setForm({
      row,
      cleared_date: d10(row.cleared_date) || new Date().toISOString().slice(0, 10),
      remarks: row.remarks || "",
    })
    setModal(true)
  }

  const submitClear = async () => {
    if (!form.cleared_date) return toastError("Cleared date is required")
    // typed dates bypass the picker's min/max, so re-check before sending
    if (form.cleared_date > today()) return toastError("Cleared date cannot be in the future")
    const issued = d10(form.row?.issue_date)
    if (issued && form.cleared_date < issued) {
      return toastError(`Cleared date cannot be before the cheque was issued (${fmtDate(issued)})`)
    }
    try {
      setSaving(true)
      await clearCheque(form.row.id, { cleared_date: form.cleared_date, remarks: form.remarks })
      toastSuccess(`Cheque ${form.row.cheque_no || ""} marked cleared`)
      setModal(false)
      await load()
    } catch (e) {
      toastError(e?.message || "Failed to record clearance")
    } finally {
      setSaving(false)
    }
  }

  const rows = data.rows || []
  const exportCsv = useMemo(() => () => {
    const head = ["S.No", "Issue Date", "Cheque No", "Issued To", "Remarks", "Cheque Date", "Cleared Date", "Status", "Amount"]
    const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`
    const lines = rows.map((r, i) =>
      [i + 1, fmtDate(r.issue_date), r.cheque_no, r.issued_to, r.remarks, fmtDate(r.cheque_date), fmtDate(r.cleared_date), r.status, r.amount].map(esc).join(",")
    )
    const csv = [head.map(esc).join(","), ...lines, esc("") + `,,,,,,,Total,${data.total}`].join("\n")
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `Cheques_Issued.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }, [rows, data.total])

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between mb-2">
          <Breadcrumbs
            title="Finance"
            breadcrumbObject={[
              { title: "Finance", link: "/finance" },
              { title: "Cheque Reconciliation", link: "/finance-cheque-tracking" },
            ]}
          />
        </div>
      <Card>
        <CardBody>
          <Row className="align-items-end g-2 mb-3">
            <Col md={2}>
              <label className="form-label small text-muted mb-1">Status</label>
              <Input type="select" bsSize="sm" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="cleared">Cleared</option>
              </Input>
            </Col>
            <Col md={2}>
              <label className="form-label small text-muted mb-1">Year</label>
              <Input type="select" bsSize="sm" value={year} onChange={e => setYear(e.target.value)}>
                <option value="">All</option>
                {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </Input>
            </Col>
            <Col md={4}>
              <label className="form-label small text-muted mb-1">Search</label>
              <Input bsSize="sm" placeholder="Payee, cheque no, remarks…" value={search} onChange={e => setSearch(e.target.value)} />
            </Col>
            <Col md={4} className="text-end">
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={exportCsv} disabled={!rows.length}>
                <i className="bx bx-export me-1" /> Export
              </button>
              <span className="text-muted small">Total&nbsp;</span>
              <span className="fw-bold" style={{ fontSize: 18, color: "#16a34a" }}>{money(data.total)}</span>
            </Col>
          </Row>

          <div style={{ overflowX: "auto" }}>
            <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>S.No</th>
                  <th style={{ width: 95 }}>Issue Date</th>
                  <th style={{ minWidth: 230 }}>Cheque No / Issued To</th>
                  <th style={{ minWidth: 180 }}>Remarks</th>
                  <th style={{ width: 100 }}>Cheque Date</th>
                  <th style={{ width: 100 }}>Cleared Date</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th className="text-end" style={{ width: 110 }}>Amount</th>
                  <th className="text-end" style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="9" className="text-center py-4"><Spinner size="sm" /> Loading…</td></tr>}
                {!loading && !rows.length && <tr><td colSpan="9" className="text-center text-muted py-4">No cheques found.</td></tr>}
                {!loading && rows.map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{i + 1}</td>
                    <td className="text-nowrap">{fmtDate(r.issue_date)}</td>
                    <td className="fw-500">
                      {r.cheque_no || "—"}
                      {r.duplicate && (
                        <span
                          className="badge bg-warning-subtle text-warning ms-1"
                          style={{ fontSize: 10 }}
                          title="This cheque number is used on more than one payment — check the vouchers"
                        >
                          Duplicate
                        </span>
                      )}
                      <small className="text-muted d-block">
                        {r.issued_to || "—"}
                      </small>
                    </td>
                    <td>{r.remarks || <span className="text-muted">—</span>}</td>
                    <td className="text-nowrap">{fmtDate(r.cheque_date)}</td>
                    <td className="text-nowrap">{fmtDate(r.cleared_date)}</td>
                    <td><span className="fw-500" style={{ color: statusColor(r.status) }}>{r.status}</span></td>
                    <td className="text-end fw-500">{money(r.amount)}</td>
                    <td className="text-end">
                      {r.status === "Cleared" ? (
                        <Button size="sm" color="link" className="p-0" onClick={() => openClear(r)} title="Edit clearance">
                          <i className="bx bx-edit" />
                        </Button>
                      ) : (
                        <Button size="sm" color="success" outline onClick={() => openClear(r)}>Clear</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && !!rows.length && (
                  <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                    <td className="fw-600" colSpan="7">Total ({data.count})</td>
                    <td className="text-end fw-600">{money(data.total)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>
          {form.row?.status === "Cleared" ? "Edit Clearance" : "Clear Cheque"}
        </ModalHeader>
        <ModalBody>
          {form.row && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8fafc", fontSize: 13 }}>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Cheque No</span>
                <span className="fw-500">{form.row.cheque_no || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Issued To</span>
                <span className="fw-500 text-end">{form.row.issued_to || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Amount</span>
                <span className="fw-600">{money(form.row.amount)}</span>
              </div>
            </div>
          )}
          <FormGroup>
            <Label className="small">Cleared Date <span className="text-danger">*</span></Label>
            <Input
              type="date"
              bsSize="sm"
              min={d10(form.row?.issue_date) || undefined}
              max={today()}
              value={form.cleared_date}
              onChange={e => setForm(f => ({ ...f, cleared_date: e.target.value }))}
            />
            <small className="text-muted">
              Between the issue date ({fmtDate(form.row?.issue_date)}) and today — a cheque cannot
              clear before it was issued, or on a future date.
            </small>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="small">Remarks</Label>
            <Input
              type="textarea"
              rows={3}
              bsSize="sm"
              placeholder="Clearance note…"
              value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="success" size="sm" onClick={submitClear} disabled={saving}>
            {saving ? "Saving…" : "Mark Cleared"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ChequeTracking
